import {
  CTCSS_TONES,
  DTCS_CODES,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 12
const MEMORY_SIZE = 0xd8
const CHANNEL_COUNT = 16
const FREQUENCY_BASE = 0x02000000

export type RT21HProfile = {
  protocol: 'retevis-rt21h'
  id: string
  vendor: 'Retevis'
  model: 'RT21H'
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: number
  nameLength: 0
  memorySize: number
  powerLevels: readonly ['Low', 'High']
}

export const RT21H_PROFILES: readonly RT21HProfile[] = [
  {
    protocol: 'retevis-rt21h',
    id: 'retevis-rt21h',
    vendor: 'Retevis',
    model: 'RT21H',
    summary: 'Retevis RT21H compact business radio.',
    tags: ['Experimental', 'Read + write', '16 channels', 'No channel names'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: CHANNEL_COUNT,
    nameLength: 0,
    memorySize: MEMORY_SIZE,
    powerLevels: ['Low', 'High'],
  },
]

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'Low',
    skip: false,
  }
}

function decode24(raw: Uint8Array): number {
  return (raw[0] | (raw[1] << 8) | (raw[2] << 16)) + FREQUENCY_BASE
}

function encode24(value: number): Uint8Array {
  const raw = (value / 10) - FREQUENCY_BASE
  return Uint8Array.of(
    raw & 0xff,
    (raw >> 8) & 0xff,
    (raw >> 16) & 0xff,
  )
}

function decodeTone(raw: Uint8Array): Pick<
  WebChannel,
  'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'
> {
  if (raw.every((value) => value === 0xff)) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  const value = raw[0] | (raw[1] << 8)
  if (value & 0x8000) {
    return {
      toneMode: 'dtcs',
      ctcss: null,
      dtcs: Number.parseInt((value & 0x1ff).toString(8), 10),
      dtcsPolarity: value & 0x4000 ? 'R' : 'N',
    }
  }
  return {
    toneMode: 'ctcss',
    ctcss: value / 10,
    dtcs: null,
    dtcsPolarity: 'N',
  }
}

function encodeTone(channel: WebChannel): readonly [number, number] {
  let value = 0xffff
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    value = Math.round(channel.ctcss * 10)
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    value =
      0x8000 |
      Number.parseInt(channel.dtcs.toString(), 8) |
      (channel.dtcsPolarity === 'R' ? 0x4000 : 0)
  }
  return [value & 0xff, (value >> 8) & 0xff]
}

export function parseRT21HChannel(
  image: Uint8Array,
  profile: RT21HProfile,
  number: number,
): WebChannel {
  const raw = image.slice(
    (number - 1) * BLOCK_SIZE,
    number * BLOCK_SIZE,
  )
  if (raw.byteLength !== BLOCK_SIZE || raw[0] === 0xff) {
    return emptyChannel(number)
  }
  const txRaw = raw.slice(3, 6)
  const txFrequency = txRaw.every((value) => value === 0xff)
    ? null
    : decode24(txRaw) * 10
  const rxTone = decodeTone(raw.slice(6, 8))
  const txTone = decodeTone(raw.slice(8, 10))
  const selected = txTone.toneMode === 'none' ? rxTone : txTone
  return {
    number,
    empty: false,
    name: '',
    rxFrequency: decode24(raw.slice(0, 3)) * 10,
    txFrequency,
    ...selected,
    mode: raw[10] & 0x20 ? 'NFM' : 'FM',
    power: raw[10] & 0x10 ? 'High' : 'Low',
    skip: (raw[10] & 0x08) !== 0,
  }
}

export function parseRT21HChannels(
  image: Uint8Array,
  profile: RT21HProfile,
): WebChannel[] {
  return Array.from({ length: CHANNEL_COUNT }, (_, index) =>
    parseRT21HChannel(image, profile, index + 1),
  )
}

export function applyRT21HChannel(
  image: Uint8Array,
  profile: RT21HProfile,
  channel: WebChannel,
): void {
  const start = (channel.number - 1) * BLOCK_SIZE
  const raw = image.subarray(start, start + BLOCK_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  raw.fill(0)
  image.set(encode24(channel.rxFrequency), start)
  image.set(
    encode24(channel.txFrequency ?? channel.rxFrequency),
    start + 3,
  )
  const tone = encodeTone(channel)
  raw.set(tone, 6)
  raw.set(tone, 8)
  raw[10] =
    (raw[10] & 0xc7) |
    (channel.skip ? 0x08 : 0) |
    (channel.power === 'High' ? 0x10 : 0) |
    (channel.mode === 'NFM' ? 0x20 : 0)
  raw[11] = 0
}

async function enterProgrammingMode(
  transport: RadioTransport,
): Promise<void> {
  await transport.discardInput()
  await transport.write(Uint8Array.of(0x02))
  await transport.write(new TextEncoder().encode('T210GAM'))
  if ((await transport.readExact(1, 1600))[0] !== ACK) {
    throw new Error('The radio refused RT21H programming mode.')
  }
  await transport.write(Uint8Array.of(0x02))
  const identity = await transport.readExact(8, 2000)
  if (!new TextDecoder().decode(identity).startsWith('SMP558')) {
    throw new Error('The connected radio does not identify as Retevis RT21H.')
  }
  await transport.write(Uint8Array.of(ACK))
  if ((await transport.readExact(1, 1500))[0] !== ACK) {
    throw new Error('The radio refused the RT21H clone session.')
  }
}

export async function readRT21HRadioWithTransport(
  profile: RT21HProfile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enterProgrammingMode(transport)
  try {
    const image = new Uint8Array(profile.memorySize)
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (
      let address = 0;
      address < MEMORY_SIZE;
      address += BLOCK_SIZE
    ) {
      const command = Uint8Array.of(0x52, 0, address, BLOCK_SIZE)
      await transport.write(command)
      const reply = await transport.readExact(BLOCK_SIZE + 4, 2500)
      if (
        reply[0] !== 0x57 ||
        reply[1] !== 0 ||
        reply[2] !== address ||
        reply[3] !== BLOCK_SIZE
      ) {
        throw new Error(`Unexpected RT21H reply at 0x${address.toString(16)}.`)
      }
      image.set(reply.slice(4), address)
      onProgress?.({
        current: address / BLOCK_SIZE + 1,
        total,
        label: 'Reading Retevis RT21H memory',
      })
    }
    return { image, reportedModel: 'RT21H' }
  } finally {
    await transport.write(new TextEncoder().encode('E'))
  }
}

export async function writeRT21HRadioWithTransport(
  profile: RT21HProfile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < profile.memorySize) {
    throw new Error('The selected image is smaller than RT21H memory.')
  }
  await enterProgrammingMode(transport)
  try {
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (
      let address = 0;
      address < MEMORY_SIZE;
      address += BLOCK_SIZE
    ) {
      const packet = new Uint8Array(BLOCK_SIZE + 4)
      packet.set([0x57, 0, address, BLOCK_SIZE])
      packet.set(image.slice(address, address + BLOCK_SIZE), 4)
      await transport.write(packet)
      if ((await transport.readExact(1, 1800))[0] !== ACK) {
        throw new Error(
          `The radio rejected RT21H block 0x${address.toString(16)}.`,
        )
      }
      onProgress?.({
        current: address / BLOCK_SIZE + 1,
        total,
        label: 'Writing Retevis RT21H memory',
      })
    }
  } finally {
    await transport.write(new TextEncoder().encode('E'))
  }
}
